import React from 'react';
import { motion } from 'framer-motion';
import { FaBriefcase, FaCalendarAlt, FaMapMarkerAlt, FaCode, FaRocket } from 'react-icons/fa';

const Experience: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const experiences = [
    {
      title: 'Web Developer',
      company: 'PT Bio Farma (Persero)',
      location: 'Bandung, Indonesia - Office',
      period: 'Oct 2024 - Present',
      description: 'Designing and developing enterprise applications with Frappe, Laravel, and Magento, specializing in system integration, cloud deployment on GCP, and workflow automation.',
      achievements: [
        'Developed EMR and OMS applications using Frappe (Python) and Laravel.',
        'Built custom APIs and integrated internal systems with Dynamics AX/D365 via REST and SOAP.',
        'Deployed applications on GCP and automated workflows using n8n.',
        'Led vendor coordination for B2B e-commerce system revamp with Magento and Frappe ERPNext.',
        'Planned system architecture and migrated data from the legacy platform to the new tech stack.'
      ],
      technologies: ['JavaScript', 'Python', 'PHP', 'Magento', 'Frappe', 'GCP', 'n8n', 'REST', 'SOAP'],
      type: 'Current'
    },
    {
      title: 'Frontend Developer',
      company: 'PT Bio Farma (Persero)',
      location: 'Bandung, Indonesia - Office',
      period: 'Apr 2024 - Oct 2024',
      description: 'Maintained and improved B2B e-commerce systems, integrated SAP HANA data, and explored ERPNext for future system enhancements.',
      achievements: [
        'Maintained and bug-fixed legacy B2B e-commerce applications for pharmaceutical distribution.',
        'Provided technical support and handled operational issue resolutions.',
        'Integrated SAP HANA data into surrounding systems using n8n.',
        'Assisted in vendor selection for the e-commerce platform revamp project.',
        'Researched Frappe and ERPNext frameworks for system improvement initiatives.'
      ],
      technologies: ['JavaScript', 'Python', 'PHP', 'Magento', 'Frappe', 'GCP', 'n8n', 'REST', 'SOAP', 'Next.js'],
      type: 'Previous'
    },
    {
      title: 'Web Developer',
      company: 'PT Neuronworks Indonesia',
      location: 'Bandung, Indonesia - Hybrid',
      period: 'May 2023 - Mar 2024',
      description: 'Developed web applications with Laravel, Zend, and React TypeScript, enforced code standards with Git Hooks, and implemented automated testing with Cypress and PHPUnit.',
      achievements: [
        'Developed web applications using PHP Zend Framework and Laravel with React TypeScript.',
        'Implemented GIT Hooks to enforce code standards within the development team.',
        'Built automated tests using Cypress, PHPUnit, and PEST.',
        'Applied OWASP security best practices during development.'
      ],
      technologies: ['Laravel 10', 'Zend Framework', 'React TypeScript', 'TanStack Query', 'Tailwind CSS', 'GIT', 'Cypress', 'OWASP', 'PHPUnit', 'PEST'],
      type: 'Previous'
    },
    {
      title: 'Frontend Web Developer',
      company: 'PT Kreasi Inovasi Digital (WorkLifeBeyond) - Contract',
      location: 'Bandung, Indonesia - Remote',
      period: 'Jan 2020 - May 2023',
      description: 'Developed and maintained Human Capital ecosystem applications, delivering modules for KPI management, e-learning, and task workflows with GraphQL and responsive UI design.',
      achievements: [
        'Developed and maintained Human Capital (HC) Ecosystem web applications.',
        'Built modules for event calendars, KPI management, task management (drag-and-drop), e-learning, and mini quizzes.',
        'Integrated GraphQL APIs with Apollo Client and designed responsive UIs using Tailwind CSS and Styled Components.',
        'Handled bug fixing and feature enhancements to maintain application stability.',
      ],
      technologies: ['React Javascript', 'Typescript', 'GraphQL', 'Apollo Client', 'Tailwind CSS', 'Styled Components'],
      type: 'Previous'
    },
    {
      title: 'Co-Founder',
      company: 'Foodritious - Self-Employed',
      location: 'Bandung, Indonesia - Office',
      period: 'Jul 2018 - Jun 2020',
      description: 'Built Foodritious, a health-focused web application featuring personalized diet plans, nutritional insights, and responsive UI with Laravel and Bootstrap.',
      achievements: [
        'Developed a web-based health and nutrition application, Foodritious, to help users access information on healthy food, nutritional content, recipes, and personalized diet plans.',
        'Designed and implemented the application backend using Laravel 5.8 framework with OAuth 2 authentication.',
        'Created responsive frontend layouts with Bootstrap 4 and managed project versioning with GIT.',
      ],
      technologies: ['Laravel 5.8', 'OAuth 2', 'Bootstrap 4', 'GIT'],
      type: 'Previous'
    },  
    {
      title: 'Frontend Web Developer',
      company: 'PT. Nusantara Sejahtera Investama - Internship',
      location: 'Jakarta, Indonesia - Office',
      period: 'Jun 2019 - Aug 2019',
      description: 'Built and maintained company web applications with Angular, ensuring responsive design through Bootstrap and CSS optimization.',
      achievements: [
        'Developed company websites using Angular JS, Angular 4, and Angular 7.',
        'Created responsive web layouts using Bootstrap and CSS Minifier.',
      ],
      technologies: ['Angular JS', 'Angular 4, and 7', 'Bootstrap', 'GIT'],
      type: 'Previous'
    },
    {
      title: 'Fullstack Web Developer',
      company: 'Teenagers Institute · Freelance',
      location: 'Bandung, Indonesia',
      period: 'Mar 2017 - Feb 2018',
        description: 'Developed an event management platform for youth training programs and forums using Laravel framework.',
      achievements: [],
      technologies: [ 'Laravel 5.2', 'Bootstrap 3'],
      type: 'Previous'
    },
    {
      title: 'Management Information System',
      company: 'PT Bio Farma (Persero) - Internship',
      location: 'Bandung, Indonesia - Office',
      period: 'Jan 2015 - Mar 2015',
      description: 'Developed a web-based payroll application using ASP.NET C# with DevExpress UI components.',
      achievements: [],
      technologies: ['ASP.NET C#', 'DevExpress', 'SQL Server'],
      type: 'Previous'
    },
  ];

  const education = [
    {
      degree: 'Bachelor of Information System',
      institution: 'Telkom University',
      period: '2016 - 2020',
      description: 'Focused on software engineering, database systems, and web development',
      achievements: [
        'Graduated with honors (GPA: 3.52/4.0)',
        'Completed a final project by founding a startup concept, Foodritious, a web-based health and nutrition platform.',
        'Active member of information system student organization',
        'Participated in multiple programming competitions',
        '1st Place in Nutrition App Competition at Institut Pertanian Bogor',
      ]
    }
  ];

  return (
    <section id="experience" className="section-padding bg-gray-50">
      <div className="container-custom">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="gradient-text">Experience & Education</span>
          </motion.h2>
          <motion.div
            variants={itemVariants}
            className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto mb-8"
          />
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            My professional journey and educational background in technology
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Experience Timeline */}
          <div className="lg:col-span-2">
            <motion.h3
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-2xl font-bold text-gray-800 mb-8 flex items-center"
            >
              <FaBriefcase className="text-blue-600 mr-3" />
              Professional Experience
            </motion.h3>

            <div className="space-y-8">
              {experiences.map((exp, index) => (
                <motion.div
                  key={exp.title}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Timeline Line */}
                  <div className="absolute left-8 top-12 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-cyan-500"></div>
                  
                  {/* Timeline Dot */}
                  <div className={`absolute left-6 top-12 w-4 h-4 rounded-full ${
                    exp.type === 'Current' ? 'bg-blue-600' : 'bg-gray-400'
                  }`}></div>

                  <div className="ml-16 bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                      <div>
                        <h4 className="text-xl font-bold text-gray-800 mb-1">{exp.title}</h4>
                        <div className="flex items-center text-blue-600 font-semibold mb-2">
                          <FaCode className="mr-2" />
                          {exp.company}
                        </div>
                        <div className="flex items-center text-gray-600 text-sm mb-1">
                          <FaMapMarkerAlt className="mr-2" />
                          {exp.location}
                        </div>
                        <div className="flex items-center text-gray-600 text-sm">
                          <FaCalendarAlt className="mr-2" />
                          {exp.period}
                        </div>
                      </div>
                      {exp.type === 'Current' && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium mt-2 md:mt-0">
                          Current
                        </span>
                      )}
                    </div>

                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {exp.description}
                    </p>

                    <div className="mb-4">
                      <h5 className="font-semibold text-gray-800 mb-2">{exp.achievements.length > 0 ? 'Key Achievements:' : ''}</h5>
                      <ul className="space-y-1">
                        {exp.achievements.map((achievement, achIndex) => (
                          <li key={achIndex} className="flex items-start text-sm text-gray-600">
                            <FaRocket className="text-blue-500 mr-2 mt-1 flex-shrink-0" />
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-semibold text-gray-800 mb-2">Technologies Used:</h5>
                      <div className="flex flex-wrap gap-2">
                        {exp.technologies.map((tech, techIndex) => (
                          <span
                            key={techIndex}
                            className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Education Sidebar */}
          <div className="lg:col-span-1">
            <motion.h3
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-2xl font-bold text-gray-800 mb-8 flex items-center"
            >
              <FaCalendarAlt className="text-blue-600 mr-3" />
              Education
            </motion.h3>

            {education.map((edu, index) => (
              <motion.div
                key={edu.degree}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 mb-6"
              >
                <h4 className="text-lg font-bold text-gray-800 mb-2">{edu.degree}</h4>
                <div className="text-blue-600 font-semibold mb-2">{edu.institution}</div>
                <div className="text-gray-600 text-sm mb-3">{edu.period}</div>
                <p className="text-gray-600 text-sm mb-4">{edu.description}</p>
                
                <div>
                  <h5 className="font-semibold text-gray-800 mb-2 text-sm">Achievements:</h5>
                  <ul className="space-y-1">
                    {edu.achievements.map((achievement, achIndex) => (
                      <li key={achIndex} className="text-xs text-gray-600 flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}

            {/* Skills Summary */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-6 text-white"
            >
              <h4 className="text-lg font-bold mb-4">Quick Stats</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Years Experience</span>
                  <span className="font-semibold">5+</span>
                </div>
                <div className="flex justify-between">
                  <span>Projects Completed</span>
                  <span className="font-semibold">20+</span>
                </div>
                <div className="flex justify-between">
                  <span>Technologies</span>
                  <span className="font-semibold">5+</span>
                </div>
                <div className="flex justify-between">
                  <span>Client Satisfaction</span>
                  <span className="font-semibold">100%</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;

